/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

'use strict';

import * as angular from 'angular';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { ProjectNotifier } from 'src/app/services/project-notifier.service';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';

import firebase from 'firebase/compat/app';

//todo: add a watch function to the user generation/ project generation stuff that allows the generated
//array to be updated when new entires are added

angular.module('ticketspaceApp').factory('firebaseUsersGetters', [
	'$q',
	'$firebaseObject',
	'fbConnection',
	'projectNotifier',
	function ($q, $firebaseObject, fbConnection, projectNotifier: ProjectNotifier) {
		const logger = LoggerServiceFactory.create('firebaseUserGetters');
		//## Manage the users list

		//handle the users list promise
		let usersDefer = $q.defer();
		let deferResolved = false;
		let _users;
		//tracks the user count
		let usersListCount = 0;

		//handles the actual users promise, and maps it to the one returned
		function handlePromise(resolve) {
			// stale user ref bug is probably caused here
			// need some way of handling the case where several state changes happen
			// and they resolve in a manner that causes the user list to be out of date
			// for brief periods of time, perhaps rejecting promises or something...

			if (lastProjectId !== resolve.projectId) {
				logger.warn('extra user request');
				usersDefer.reject('extra user request');
				return;
			}

			_users = resolve.users;
			deferResolved = true;
			usersDefer.resolve(_users);
		}

		//manage the promise state, update users list when project changes
		let lastProjectId = null;
		projectNotifier.projectState.subscribe(function (state) {
			if (state.projectId && lastProjectId !== state.projectId) {
				lastProjectId = state.projectId;
				if (deferResolved) {
					usersDefer = $q.defer();
				}
				usersForProject(lastProjectId).then(handlePromise);
			}
		});

		//todo performance - cache generated initials/ names, empty on $$update
		let uFactory = $firebaseObject.$extend({
			//consider returning a size itentifier with the text so the font can be scaled according to the number of chars
			initials() {
				if (this.$value === null) {
					return 'XX';
				}
				if (!this.email) {
					return 'XX';
				}
				if (!this.firstName && !this.lastName) {
					return this.email[0] + this.email[1];
				}
				if (!this.lastName) {
					return this.firstName[0];
				}
				if (!this.firstName) {
					return this.lastName[0];
				}

				return this.firstName[0] + this.lastName[0];
			},

			parsedName() {
				if (this.$value === null) {
					return 'userless';
				}
				if (!this.firstName && !this.lastName) {
					return this.email;
				}
				return (this.firstName ? this.firstName : '') + ' ' + (this.lastName ? this.lastName : '');
			},

			$$updated(snap) {
				// apply the changes using, here we'll just use the generic "updateRecord"

				//checking if the id key has works here because security rules
				//prevent it from being different then $id, it might be better to
				//compare the $id against the current list though

				updateCount(this.id, snap.child('id').val());

				return $firebaseObject.prototype.$$updated.apply(this, arguments);
				//if(snap.exists()){ usersListCount++; }
				//else{ usersListCount--; }
				//this = snap.val();
			},

			$$error(err) {},
		});

		function updateCount(old, current) {
			if ((!old && !current) || old === current) {
			} else if (old) {
				usersListCount--;
			} else if (current) {
				usersListCount++;
			}
			//logger.log('old', old, 'new', current, usersListCount);
		}

		//#### usersForProject(projectId)
		//returns a promise resolving to a list of users
		function usersForProject(projectId) {
			if (!projectId) {
				return $q.reject();
			}

			usersListCount = 0;
			let defer = $q.defer();

			//truly an astounding degree of javascript bs
			let userList: any = {};
			userList.users = Object.create({
				get length() {
					return usersListCount;
				},
			});

			const projectPath = fancyFirebaseGetters.getProjectsPath(projectId);
			let userIdList = fbConnection.fbArray(
				fancyFirebaseReferencer
					.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
					.child('users')
			);
			userIdList.$loaded().then(function () {
				let batch = fbConnection.batch();
				userIdList.forEach(function (userId) {
					const usersPath = fancyFirebaseGetters.getUsersPath(userId.$id);
					let user = new uFactory(
						fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(usersPath, projectId)
					);
					batch.push(
						user.$loaded().then(function () {
							userList.users[user.id] = user;
						})
					);
				});
				batch.end().then(function () {
					//logger.log('userList', userList.users, userList, projectId);
					defer.resolve({ users: userList, projectId });

					userIdList.$watch(function (change) {
						if (change.event === 'child_removed') {
							if (userList.users[change.key]) {
								//logger.log('change', change.key, userList.users[change.key], userList.users);
								userList.users[change.key].$destroy();
								usersListCount--;
								delete userList.users[change.key];
							}
						} else if (change.event === 'child_added') {
							if (!userList.users[change.key]) {
								//userList.users[change.key]
								const usersPath = fancyFirebaseGetters.getUsersPath(change.key);
								let user = new uFactory(
									fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(usersPath)
								);
								user.$loaded().then(function () {
									userList.users[user.id] = user;
								});
							}
						}
					});
				});
			});
			return defer.promise;
		}

		function userFromUserId(userId, waitForUserToPopulate) {
			const usersPath = fancyFirebaseGetters.getUsersPath(userId);
			let user = new uFactory(fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(usersPath));
			let promise = user.$loaded().then(function () {
				return user;
			});
			if (!waitForUserToPopulate) {
				return promise;
			}

			//special case for the creating a user flow
			//which can resolve before the user object exists
			else {
				//logger.log("waiting for user");
				return promise.then(function (user) {
					//logger.log('user loaded', user.id, user);
					if (user.$value === null) {
						logger.log('user data is null');
					}
					if (user.id) {
						return user;
					} else {
						//Logging.debug("this is where it would have failed");
						//using defer to prevent context switching...
						//on the off chance that's causing something
						let defer = $q.defer();

						let unwatch = user.$watch(function () {
							if (user.$value !== null) {
								//Logging.debug("...and now it didn't");
								unwatch();
								defer.resolve(user);
							}
						});

						return defer.promise;
					}
				});
			}
		}

		return {
			getUsers() {
				return usersDefer.promise;
			},
			getUsersSync() {
				if (!deferResolved) {
					return false;
				}
				return _users;
			},
			userFromUserId,
		};
	},
]);
